import { FC } from "react";

import { t } from "i18n/i18n";
import { Switch } from "mds/components/ToggleSwitch";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useAssessmentGradingFormContext } from "providers/AssessmentGradingFormProvider";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectFeedbackStyleSampleCount,
  selectIsAssessWithAIModeEnabled,
  selectIsDerivingFeedbackStyle,
  selectIsRecordingFeedbackStyle,
} from "store/selectors";
import { aiStateActions } from "store/slices/ai";
import { stopRecordingFeedbackStyle } from "utils/assessment-feedback-style-recording";
import { isAssessmentAIFeedbackStyleEnabled } from "utils/waffle-flags";

/** Record manual feedback text for style inference; only used on the assessment grading form. */
export const FeedbackStyleRecordingSwitch: FC = () => {
  const dispatch = useAppDispatch();
  const isRecording = useAppSelector(selectIsRecordingFeedbackStyle);
  const recordedCount = useAppSelector(selectFeedbackStyleSampleCount);
  const isBusy = useAppSelector(selectIsDerivingFeedbackStyle);
  const isAssessWithAIModeEnabled = useAppSelector(selectIsAssessWithAIModeEnabled);
  const { isAiAssessmentLoading, isSubmitting } = useAssessmentGradingFormContext();

  if (!isAssessmentAIFeedbackStyleEnabled()) {
    return null;
  }

  // Mutual exclusion with Auto AI: recording needs manual feedback; Auto AI fills it.
  const isDisabled = isAiAssessmentLoading || isSubmitting || isBusy || isAssessWithAIModeEnabled;
  const showAsRecording = isRecording && !isAssessWithAIModeEnabled;

  const onToggle = async (next: boolean) => {
    if (isDisabled) {
      return;
    }
    if (next) {
      dispatch(aiStateActions.setIsRecordingFeedbackStyle(true));
      return;
    }
    if (!isRecording) {
      return;
    }
    try {
      await stopRecordingFeedbackStyle(dispatch);
    } catch {
      // Toasts in stopRecordingFeedbackStyle
    }
  };

  return (
    <div className="mb-0 mt-2 flex w-full flex-col gap-0.5">
      <SquareButton
        className="w-full justify-between"
        disabled={isDisabled}
        eventName="Assessment - Record feedback style toggle"
        isSelected={showAsRecording}
        kind="toggle"
        size="m"
        onClick={() => onToggle(!isRecording)}
      >
        {t("home.settings.assessment_ai_feedback_style.recording_toggle")}
        <Switch checked={showAsRecording} disabled={isDisabled} />
      </SquareButton>
      <p className="body-xs !mt-0 text-black-tint-55">
        {isAssessWithAIModeEnabled
          ? t("home.settings.assessment_ai_feedback_style.recording_disabled_by_auto_ai")
          : t("home.settings.assessment_ai_feedback_style.recording_description")}
      </p>
      {showAsRecording && recordedCount > 0 && (
        <p className="body-xs !mt-0 text-black-tint-55">
          {t("home.settings.assessment_ai_feedback_style.recording_count", {
            count: recordedCount,
          })}
        </p>
      )}
    </div>
  );
};
